import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, Button, Space, Modal, Form, Input, Switch,
    message, Popconfirm, Typography, Tag, Badge, Tooltip,
    Row, Col, Select, InputNumber, Flex, theme, Divider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    ShopOutlined, CheckCircleOutlined, StopOutlined,
    CloseOutlined, SearchOutlined, FilterOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchShopifyCategories,
    createShopifyCategory,
    updateShopifyCategory,
    deleteShopifyCategory,
    searchCategories,
    filterByStatus,
    filterByKeywordType,
    changePagination,
    clearFilters,
    fetchAvailableKeywords
} from '../actions';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ShopifyCategorySettings = () => {
    const dispatch = useDispatch();
    const { token } = theme.useToken();

    const shopifyCategoriesState = useSelector(state => state.shopifyCategories || {
        categories: [],
        loading: false,
        pagination: { current: 1, pageSize: 50, total: 0 },
        filters: { searchText: '', statusFilter: 'all', keywordTypeFilter: 'all' },
        availableKeywords: { title: [], tag: [], vendor: [], productType: [] }
    });

    const { categories = [], loading = false, pagination = {}, filters = {}, availableKeywords = {} } = shopifyCategoriesState;

    const [modalVisible, setModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [form] = Form.useForm();

    // Local state for filters
    const [localSearchText, setLocalSearchText] = useState(filters.searchText || '');
    const [localStatusFilter, setLocalStatusFilter] = useState(filters.statusFilter || 'all');
    const [localKeywordTypeFilter, setLocalKeywordTypeFilter] = useState(filters.keywordTypeFilter || 'all');

    // State for keyword tags
    const [titleKeywordTags, setTitleKeywordTags] = useState([]);
    const [tagKeywordTags, setTagKeywordTags] = useState([]);
    const [vendorKeywordTags, setVendorKeywordTags] = useState([]);
    const [productTypeKeywordTags, setProductTypeKeywordTags] = useState([]);
    const [variantTitleKeywordTags, setVariantTitleKeywordTags] = useState([]);

    // Tag input states
    const [titleInputVisible, setTitleInputVisible] = useState(false);
    const [titleInputValue, setTitleInputValue] = useState('');
    const [tagInputVisible, setTagInputVisible] = useState(false);
    const [tagInputValue, setTagInputValue] = useState('');
    const [vendorInputVisible, setVendorInputVisible] = useState(false);
    const [vendorInputValue, setVendorInputValue] = useState('');
    const [productTypeInputVisible, setProductTypeInputVisible] = useState(false);
    const [productTypeInputValue, setProductTypeInputValue] = useState('');
    const [variantTitleInputVisible, setVariantTitleInputVisible] = useState(false);
    const [variantTitleInputValue, setVariantTitleInputValue] = useState('');

    const titleInputRef = useRef(null);
    const tagInputRef = useRef(null);
    const vendorInputRef = useRef(null);
    const productTypeInputRef = useRef(null);
    const variantTitleInputRef = useRef(null);

    // Load data on mount
    useEffect(() => {
        if (categories.length === 0) {
            dispatch(fetchShopifyCategories({ page: 1, pageSize: 50 }));
        }
        dispatch(fetchAvailableKeywords());
    }, [dispatch, categories.length]);

    // Sync local state with Redux filters
    useEffect(() => {
        setLocalSearchText(filters.searchText || '');
        setLocalStatusFilter(filters.statusFilter || 'all');
        setLocalKeywordTypeFilter(filters.keywordTypeFilter || 'all');
    }, [filters]);

    // Tag management functions
    const handleTagClose = (setter, tags, removedTag) => {
        setter(tags.filter(tag => tag !== removedTag));
    };

    const handleTagInputConfirm = (value, tags, setter, setVisible, setValue) => {
        if (value && !tags.includes(value)) {
            setter([...tags, value]);
        }
        setVisible(false);
        setValue('');
    };

    useEffect(() => {
        if (titleInputVisible) titleInputRef.current?.focus();
    }, [titleInputVisible]);

    useEffect(() => {
        if (tagInputVisible) tagInputRef.current?.focus();
    }, [tagInputVisible]);

    useEffect(() => {
        if (vendorInputVisible) vendorInputRef.current?.focus();
    }, [vendorInputVisible]);

    useEffect(() => {
        if (productTypeInputVisible) productTypeInputRef.current?.focus();
    }, [productTypeInputVisible]);

    useEffect(() => {
        if (variantTitleInputVisible) variantTitleInputRef.current?.focus();
    }, [variantTitleInputVisible]);

    const handleCreateCategory = () => {
        setEditingCategory(null);
        form.resetFields();
        setTitleKeywordTags([]);
        setTagKeywordTags([]);
        setVendorKeywordTags([]);
        setProductTypeKeywordTags([]);
        setVariantTitleKeywordTags([]);
        setModalVisible(true);
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setTitleKeywordTags(category.titleKeywords || []);
        setTagKeywordTags(category.tagKeywords || []);
        setVendorKeywordTags(category.vendorKeywords || []);
        setProductTypeKeywordTags(category.productTypeKeywords || []);
        setVariantTitleKeywordTags(category.variantTitleKeywords || []);
        
        form.setFieldsValue({
            name: category.name,
            description: category.description,
            isActive: category.isActive,
            baseCost: category.baseCost,
            referralFeeRate: category.referralFeeRate,
            otherFee: category.otherFee,
            priority: category.priority
        });
        setModalVisible(true);
    };

    const handleDeleteCategory = async (id) => {
        try {
            const result = await dispatch(deleteShopifyCategory(id));
            
            if (result.success) {
                message.success('Xóa danh mục Shopify thành công');
                dispatch(fetchShopifyCategories({ page: pagination.current, pageSize: pagination.pageSize }));
            } else {
                message.error(result.error || 'Lỗi khi xóa danh mục Shopify');
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            message.error('Lỗi khi xóa danh mục Shopify: ' + (error.message || 'Unknown error'));
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            const categoryData = {
                ...values,
                titleKeywords: titleKeywordTags,
                tagKeywords: tagKeywordTags,
                vendorKeywords: vendorKeywordTags,
                productTypeKeywords: productTypeKeywordTags,
                variantTitleKeywords: variantTitleKeywordTags
            };

            if (editingCategory) {
                await dispatch(updateShopifyCategory(editingCategory.id, categoryData));
                message.success('Cập nhật danh mục Shopify thành công');
            } else {
                await dispatch(createShopifyCategory(categoryData));
                message.success('Tạo danh mục Shopify thành công');
            }

            setModalVisible(false);
            form.resetFields();
            dispatch(fetchShopifyCategories({ page: pagination.current, pageSize: pagination.pageSize }));
        } catch (error) {
            message.error('Lỗi khi lưu danh mục Shopify');
        }
    };

    const handleSearchChange = (value) => {
        setLocalSearchText(value);
    };

    const handleSearchSubmit = () => {
        dispatch(searchCategories(localSearchText));
    };

    const handleStatusFilterChange = (value) => {
        setLocalStatusFilter(value);
        dispatch(filterByStatus(value));
    };

    const handleKeywordTypeFilterChange = (value) => {
        setLocalKeywordTypeFilter(value);
        dispatch(filterByKeywordType(value));
    };

    const handleClearFilters = () => {
        setLocalSearchText('');
        setLocalStatusFilter('all');
        setLocalKeywordTypeFilter('all');
        dispatch(clearFilters());
    };

    const columns = [
        {
            title: 'Tên danh mục',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (text, record) => (
                <Space>
                    <ShopOutlined style={{ color: token.colorPrimary }} />
                    <Text strong>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Title Keywords',
            dataIndex: 'titleKeywords',
            key: 'titleKeywords',
            width: 200,
            render: (keywords) => (
                <>
                    {keywords && keywords.slice(0, 3).map(kw => (
                        <Tag key={kw} color="blue">{kw}</Tag>
                    ))}
                    {keywords && keywords.length > 3 && (
                        <Tag>+{keywords.length - 3}</Tag>
                    )}
                </>
            ),
        },
        {
            title: 'Tag Keywords (SKU)',
            dataIndex: 'tagKeywords',
            key: 'tagKeywords',
            width: 150,
            render: (keywords) => (
                <>
                    {keywords && keywords.slice(0, 2).map(kw => (
                        <Tag key={kw} color="green">{kw}</Tag>
                    ))}
                    {keywords && keywords.length > 2 && (
                        <Tag>+{keywords.length - 2}</Tag>
                    )}
                </>
            ),
        },
        {
            title: 'Variant Title Keywords',
            dataIndex: 'variantTitleKeywords',
            key: 'variantTitleKeywords',
            width: 180,
            render: (keywords) => (
                <>
                    {keywords && keywords.slice(0, 2).map(kw => (
                        <Tag key={kw} color="magenta">{kw}</Tag>
                    ))}
                    {keywords && keywords.length > 2 && (
                        <Tag>+{keywords.length - 2}</Tag>
                    )}
                </>
            ),
        },
        {
            title: 'Pricing',
            key: 'pricing',
            width: 150,
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {record.baseCost && <Text type="secondary">Cost: ${record.baseCost}</Text>}
                    {record.referralFeeRate && <Text type="secondary">Fee: {record.referralFeeRate}%</Text>}
                </Space>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            render: (isActive) => (
                <Badge
                    status={isActive ? 'success' : 'default'}
                    text={isActive ? 'Hoạt động' : 'Không hoạt động'}
                />
            ),
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 80,
            render: (priority) => <Tag color="cyan">{priority || 0}</Tag>,
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Sửa">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditCategory(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Xóa danh mục Shopify?"
                        description="Bạn có chắc chắn muốn xóa danh mục này?"
                        onConfirm={() => handleDeleteCategory(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Tooltip title="Xóa">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderKeywordTags = (tags, setter, inputVisible, setInputVisible, inputValue, setInputValue, inputRef, color) => (
        <Flex gap="4px 0" wrap>
            {tags.map((tag, index) => (
                <Tag
                    key={tag}
                    closable
                    onClose={() => handleTagClose(setter, tags, tag)}
                    color={color}
                >
                    {tag}
                </Tag>
            ))}
            {inputVisible ? (
                <Input
                    ref={inputRef}
                    type="text"
                    size="small"
                    style={{ width: 120 }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={() => handleTagInputConfirm(inputValue, tags, setter, setInputVisible, setInputValue)}
                    onPressEnter={() => handleTagInputConfirm(inputValue, tags, setter, setInputVisible, setInputValue)}
                />
            ) : (
                <Tag onClick={() => setInputVisible(true)} style={{ background: token.colorBgContainer, borderStyle: 'dashed', cursor: 'pointer' }}>
                    <PlusOutlined /> New Keyword
                </Tag>
            )}
        </Flex>
    );

    return (
        <div className="shopify-category-settings">
            <Row style={{ marginBottom: 16 }} gutter={16} align="middle">
                <Col flex="auto">
                    <Space size="middle">
                        <Input.Search
                            style={{ width: 300 }}
                            placeholder="Tìm kiếm theo tên hoặc keywords..."
                            allowClear
                            value={localSearchText}
                            onChange={e => handleSearchChange(e.target.value)}
                            onSearch={handleSearchSubmit}
                        />

                        <Select
                            placeholder="Trạng thái"
                            value={localStatusFilter}
                            onChange={handleStatusFilterChange}
                            style={{ width: 150 }}
                        >
                            <Option value="all">Tất cả</Option>
                            <Option value="true">Hoạt động</Option>
                            <Option value="false">Không hoạt động</Option>
                        </Select>

                        <Select
                            placeholder="Loại keyword"
                            value={localKeywordTypeFilter}
                            onChange={handleKeywordTypeFilterChange}
                            style={{ width: 150 }}
                        >
                            <Option value="all">Tất cả</Option>
                            <Option value="title">Title</Option>
                            <Option value="tag">Tags</Option>
                            <Option value="vendor">Vendor</Option>
                            <Option value="productType">Product Type</Option>
                        </Select>

                        {(localSearchText || localStatusFilter !== 'all' || localKeywordTypeFilter !== 'all') && (
                            <Button onClick={handleClearFilters}>
                                Xóa bộ lọc
                            </Button>
                        )}
                    </Space>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateCategory}
                    >
                        Tạo danh mục Shopify
                    </Button>
                </Col>
            </Row>

            <Table
                dataSource={Array.isArray(categories) ? categories.map(cat => ({ ...cat, key: cat.id })) : []}
                columns={columns}
                loading={loading}
                pagination={{
                    current: pagination.current || 1,
                    pageSize: pagination.pageSize || 50,
                    total: pagination.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} danh mục`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: (page, pageSize) => dispatch(changePagination(page, pageSize))
                }}
                scroll={{ x: 1200 }}
            />

            <Modal
                title={editingCategory ? 'Sửa danh mục Shopify' : 'Tạo danh mục Shopify mới'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                width={800}
                okText={editingCategory ? 'Cập nhật' : 'Tạo mới'}
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="isActive"
                        label="Trạng thái"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Tên danh mục"
                        rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
                    >
                        <Input placeholder="Nhập tên danh mục" prefix={<ShopOutlined />} />
                    </Form.Item>

                    <Form.Item name="description" label="Mô tả">
                        <TextArea rows={3} placeholder="Nhập mô tả chi tiết về danh mục" />
                    </Form.Item>

                    <Divider>Keywords Mapping</Divider>

                    <Form.Item label="Title Keywords" tooltip="Từ khóa trong product title">
                        {renderKeywordTags(titleKeywordTags, setTitleKeywordTags, titleInputVisible, setTitleInputVisible, titleInputValue, setTitleInputValue, titleInputRef, 'blue')}
                    </Form.Item>

                    <Form.Item label="Tag Keywords" tooltip="Từ khóa trong product tags">
                        {renderKeywordTags(tagKeywordTags, setTagKeywordTags, tagInputVisible, setTagInputVisible, tagInputValue, setTagInputValue, tagInputRef, 'green')}
                    </Form.Item>

                    <Form.Item label="Vendor Keywords" tooltip="Từ khóa trong vendor name">
                        {renderKeywordTags(vendorKeywordTags, setVendorKeywordTags, vendorInputVisible, setVendorInputVisible, vendorInputValue, setVendorInputValue, vendorInputRef, 'orange')}
                    </Form.Item>

                    <Form.Item label="Product Type Keywords" tooltip="Từ khóa trong product type">
                        {renderKeywordTags(productTypeKeywordTags, setProductTypeKeywordTags, productTypeInputVisible, setProductTypeInputVisible, productTypeInputValue, setProductTypeInputValue, productTypeInputRef, 'purple')}
                    </Form.Item>

                    <Form.Item label="Variant Title Keywords" tooltip="Từ khóa trong variant title">
                        {renderKeywordTags(variantTitleKeywordTags, setVariantTitleKeywordTags, variantTitleInputVisible, setVariantTitleInputVisible, variantTitleInputValue, setVariantTitleInputValue, variantTitleInputRef, 'magenta')}
                    </Form.Item>

                    <Divider>Pricing & Costs</Divider>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="baseCost" label="Base Cost ($)">
                                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="referralFeeRate" label="Referral Fee (%)">
                                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="otherFee" label="Other Fee ($)">
                                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="priority" label="Priority" tooltip="Độ ưu tiên khi auto-categorize (số càng lớn càng ưu tiên)">
                        <InputNumber style={{ width: '100%' }} min={0} step={1} defaultValue={0} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ShopifyCategorySettings;
